import FormUIOptions from '../components/formBuilder/formElements/formUIOptions';
import { isValue } from '../utility/isValue';

export default {
  calculations: {
    schema: [
      {
        sectionType: 'comparison',
        sectionLabel: 'Cash Flow Comparison',
        sectionValue: 'cash-flow-comparison',
        //for now u gotta put three schemas
        schemas: [
          {
            sectionLabel: 'On Purchase',
            sectionSubLabel: 'Monthly',
            annualValue: true,
            afterValues: false,
            schema: [
              {
                type: FormUIOptions.Money,
                label: 'Gross Rent',
                name: 'totalRentalIncomeOnPurchase',
                tooltipHeading: 'Gross Rent',
                description: 'Rental income across all units',
                unit: '$',
              },
              {
                type: FormUIOptions.Table,
                label: '',
                name: 'grossIncomePerUnit',
                condition: [
                  {
                    field: 'enterTotalRentalIncome',
                    value: false,
                  },
                ],
                schema: [
                  {
                    type: FormUIOptions.Text,
                    name: 'unitName',
                    label: 'Unit Name',
                    derivedFrom: ['unitName'],
                    calFunction: (name) => {
                      return `Unit ${name}`;
                    },
                  },
                  {
                    type: FormUIOptions.UnitMoney,
                    label: 'Amount per month',
                    showMonthly: true,
                    unit: '$',
                    isConstant: true,
                    displayMonthlyFrom: ['rent'],
                    displayMonthlyFunction: (rent) => {
                      return rent;
                    },
                  },
                ],
              },
              {
                type: FormUIOptions.Money,
                label: 'Vacancy',
                layout: 'default',
                name: 'vacancy',
                unit: '$',
                formula: (vacancy) => ['-', `${vacancy}`],
                displayPercentFrom: [
                  'vacancyDeduction',
                  'totalRentalIncomeOnPurchase',
                ],
                displayPercentFunction: (
                  vacancyDeduction,
                  totalRentalIncome
                ) => {
                  if (isValue(vacancyDeduction) && isValue(totalRentalIncome)) {
                    return Math.round(
                      (vacancyDeduction / totalRentalIncome) * 100
                    );
                  } else {
                    return null;
                  }
                },
              },
              {
                type: FormUIOptions.Money,
                label: 'Other Income',
                name: 'totalAdditionalIncome',
                groupEnd: true,
                tooltipHeading: 'Additional Income',
                description:
                  'Additional income except for rent across all units',
                unit: '$',
                formula: (totalAdditionalIncome) => [
                  '+',
                  `${totalAdditionalIncome}`,
                ],
              },
              {
                type: FormUIOptions.Table,
                label: '',
                name: 'otherPropertyIncome',
                condition: [
                  {
                    field: 'cashPurchase',
                    value: false,
                  },
                  {
                    field: 'enterTotalOtherIncome',
                    value: [false, undefined, ''],
                  },
                ],
                schema: [
                  {
                    type: FormUIOptions.Text,
                    name: 'otherIncomeName',
                    label: 'Other Income',
                    small: true,
                  },
                  {
                    type: FormUIOptions.UnitMoney,
                    label: 'Amount',
                    layout: 'default',
                    name: 'otherIncomeAmount',
                    unit: '$',
                    small: true,
                  },
                ],
              },
              {
                type: FormUIOptions.Money,
                label: 'Gross Income',
                name: 'monthlyGOI',
                emphasize: true,
                groupStart: true,
                tooltipHeading: 'Gross Income (GOI)',
                description:
                  'Rental income across all units adjusted for vacancy rate',
                unit: '$',
                formula: (monthlyGOI) => ['=', `${monthlyGOI}`],
                total: true,
              },
              {
                type: FormUIOptions.Money,
                label: 'Expenses',
                layout: 'default',
                name: 'totalMonthlyOperatingExpensesPurchase',
                groupEnd: true,
                unit: '$',
                formula: (totalMonthlyOperatingExpensesPurchase) => [
                  '-',
                  `${totalMonthlyOperatingExpensesPurchase}`,
                ],
              },
              {
                type: FormUIOptions.Table,
                label: '',
                name: 'operatingExpenses',
                condition: [
                  {
                    field: 'cashPurchase',
                    value: false,
                  },
                  {
                    field: 'enterTotalExpenses',
                    value: [false, undefined, ''],
                  },
                ],
                schema: [
                  {
                    type: FormUIOptions.Text,
                    name: 'expenseName',
                    label: 'Expense',
                    small: true,
                  },
                  {
                    type: FormUIOptions.UnitMoney,
                    derivedFrom: 'rentalIncomePreVacancy',
                    label: 'Amount',
                    layout: 'default',
                    name: 'expenseAmount',
                    unit: '$',
                    small: true,
                  },
                ],
              },
              {
                type: FormUIOptions.Money,
                label: 'Net Income',
                layout: 'default',
                name: 'monthlyNOI',
                groupStart: true,
                emphasize: true,
                tooltipHeading: 'Net Income (NOI)',
                description:
                  'Income from all units adjusted for total operating expenses',
                unit: '$',
                formula: (monthlyNOI) => ['=', `${monthlyNOI}`],
                total: true,
              },
              {
                type: FormUIOptions.Money,
                label: 'Loan Payment',
                name: 'monthlyDebtService',
                unit: '$',
                formula: (monthlyDebtService) => ['-', `${monthlyDebtService}`],
              },
              {
                type: FormUIOptions.Money,
                label: 'Cash Flow',
                layout: 'default',
                isAnnual: true,
                name: 'annualPreTaxCashFlow',
                emphasize: true,
                dynamic: true,
                unit: '$',
                formula: (annualPreTaxCashFlow) => [
                  '=',
                  `${annualPreTaxCashFlow}`,
                ],
              },
            ],
          },
          {
            sectionLabel: 'Buy & Hold (After Rehab)',
            sectionSubLabel: 'Monthly',
            annualValue: true,
            afterValues: true,
            schema: [
              {
                type: FormUIOptions.Money,
                label: 'Gross Rent',
                name: 'totalRentalIncomeBuyAndHold',
                tooltipHeading: 'Gross Rent',
                description: 'Rental income across all units',
                unit: '$',
              },
              {
                type: FormUIOptions.Table,
                label: '',
                name: 'grossIncomePerUnit',
                condition: [
                  {
                    field: 'enterTotalRentalIncome',
                    value: false,
                  },
                ],
                schema: [
                  {
                    type: FormUIOptions.Text,
                    name: 'unitName',
                    label: 'Unit Name',
                    derivedFrom: ['unitName'],
                    calFunction: (name) => {
                      return `Unit ${name}`;
                    },
                  },
                  {
                    type: FormUIOptions.UnitMoney,
                    label: '',
                    showMonthly: true,
                    unit: '$',
                    isConstant: true,
                    displayMonthlyFrom: ['rent'],
                    displayMonthlyFunction: (rent) => {
                      return rent;
                    },
                  },
                ],
              },
              {
                type: FormUIOptions.Money,
                label: 'Vacancy',
                layout: 'default',
                name: 'vacancy',
                unit: '$',
                formula: (vacancy) => ['-', `${vacancy}`],
                displayPercentFrom: [
                  'vacancyDeduction',
                  'totalRentalIncomeOnPurchase',
                ],
                displayPercentFunction: (
                  vacancyDeduction,
                  totalRentalIncome
                ) => {
                  if (isValue(vacancyDeduction) && isValue(totalRentalIncome)) {
                    return Math.round(
                      (vacancyDeduction / totalRentalIncome) * 100
                    );
                  } else {
                    return null;
                  }
                },
              },
              {
                type: FormUIOptions.Money,
                label: 'Other Income',
                name: 'totalAdditionalIncomeBuyAndHold',
                groupEnd: true,
                tooltipHeading: 'Additional Income',
                description:
                  'Additional income except for rent across all units',
                unit: '$',
                formula: (totalAdditionalIncome) => [
                  '+',
                  `${totalAdditionalIncome}`,
                ],
              },
              {
                type: FormUIOptions.Table,
                label: '',
                name: 'otherPropertyIncome',
                condition: [
                  {
                    field: 'cashPurchase',
                    value: false,
                  },
                  {
                    field: 'enterTotalOtherIncome',
                    value: [false, undefined, ''],
                  },
                ],
                schema: [
                  {
                    type: FormUIOptions.Text,
                    name: 'otherIncomeName',
                    label: 'Other Income',
                    small: true,
                  },
                  {
                    type: FormUIOptions.UnitMoney,
                    label: 'Amount',
                    layout: 'default',
                    name: 'otherIncomeAmount',
                    unit: '$',
                    small: true,
                  },
                ],
              },
              {
                type: FormUIOptions.Money,
                label: 'Gross Income',
                name: 'monthlyGOI',
                emphasize: true,
                groupStart: true,
                tooltipHeading: 'Gross Income (GOI)',
                description:
                  'Rental income across all units adjusted for vacancy rate',
                unit: '$',
                formula: (monthlyGOI) => ['=', `${monthlyGOI}`],
                total: true,
              },
              {
                type: FormUIOptions.Money,
                label: 'Expenses',
                layout: 'default',
                name: 'totalMonthlyOperatingExpenses',
                groupEnd: true,
                unit: '$',
                formula: (totalMonthlyOperatingExpensesPurchase) => [
                  '-',
                  `${totalMonthlyOperatingExpensesPurchase}`,
                ],
              },
              {
                type: FormUIOptions.Table,
                label: '',
                name: 'operatingExpenses',
                condition: [
                  {
                    field: 'cashPurchase',
                    value: false,
                  },
                  {
                    field: 'enterTotalExpenses',
                    value: [false, undefined, ''],
                  },
                ],
                schema: [
                  {
                    type: FormUIOptions.Text,
                    name: 'expenseName',
                    label: 'Expense',
                    small: true,
                  },
                  {
                    type: FormUIOptions.UnitMoney,
                    derivedFrom: 'rentalIncomePreVacancy',
                    label: 'Amount',
                    layout: 'default',
                    name: 'expenseAmount',
                    unit: '$',
                    small: true,
                  },
                ],
              },
              {
                type: FormUIOptions.Money,
                label: 'Net Income',
                layout: 'default',
                name: 'monthlyNOI',
                emphasize: true,
                groupStart: true,
                tooltipHeading: 'Net Income (NOI)',
                description:
                  'Income from all units adjusted for total operating expenses',
                unit: '$',
                formula: (monthlyNOI) => ['=', `${monthlyNOI}`],
                total: true,
              },
              {
                type: FormUIOptions.Money,
                label: 'Loan Payment',
                name: 'monthlyDebtServiceBuyAndHold',
                unit: '$',
                formula: (monthlyDebtService) => ['-', `${monthlyDebtService}`],
              },
              {
                type: FormUIOptions.Money,
                label: 'Cash Flow',
                layout: 'default',
                name: 'monthlyPreTaxCashFlowBuyAndHold',
                emphasize: true,
                dynamic: true,
                unit: '$',
                formula: (annualPreTaxCashFlow) => [
                  '=',
                  `${annualPreTaxCashFlow}`,
                ],
              },
            ],
          },
          {
            sectionLabel: 'BRRRR Scenario',
            sectionSubLabel: 'Monthly',
            annualValue: true,
            afterValues: true,
            schema: [
              {
                type: FormUIOptions.Money,
                label: 'Gross Rent',
                name: 'totalRentalIncome',
                tooltipHeading: 'Gross Rent',
                description: 'Rental income across all units',
                unit: '$',
              },
              {
                type: FormUIOptions.Table,
                label: '',
                name: 'grossIncomePerUnit',
                condition: [
                  {
                    field: 'enterTotalRentalIncome',
                    value: false,
                  },
                ],
                schema: [
                  {
                    type: FormUIOptions.Text,
                    name: 'unitName',
                    label: 'Unit Name',
                    derivedFrom: ['unitName'],
                    calFunction: (name) => {
                      return `Unit ${name}`;
                    },
                  },
                  {
                    type: FormUIOptions.UnitMoney,
                    label: '',
                    showMonthly: true,
                    unit: '$',
                    isConstant: true,
                    displayMonthlyFrom: ['rent'],
                    displayMonthlyFunction: (rent) => {
                      return rent;
                    },
                  },
                ],
              },
              {
                type: FormUIOptions.Money,
                label: 'Vacancy',
                layout: 'default',
                name: 'vacancy',
                unit: '$',
                formula: (vacancy) => ['-', `${vacancy}`],
                displayPercentFrom: [
                  'vacancyDeduction',
                  'totalRentalIncomeOnPurchase',
                ],
                displayPercentFunction: (
                  vacancyDeduction,
                  totalRentalIncome
                ) => {
                  if (isValue(vacancyDeduction) && isValue(totalRentalIncome)) {
                    return Math.round(
                      (vacancyDeduction / totalRentalIncome) * 100
                    );
                  } else {
                    return null;
                  }
                },
              },
              {
                type: FormUIOptions.Money,
                label: 'Other Income',
                name: 'totalOtherIncome',
                groupEnd: true,
                tooltipHeading: 'Additional Income',
                description:
                  'Additional income except for rent across all units',
                unit: '$',
                formula: (totalAdditionalIncome) => [
                  '+',
                  `${totalAdditionalIncome}`,
                ],
              },
              {
                type: FormUIOptions.Table,
                label: '',
                name: 'otherPropertyIncome',
                condition: [
                  {
                    field: 'cashPurchase',
                    value: false,
                  },
                  {
                    field: 'enterTotalOtherIncome',
                    value: [false, undefined, ''],
                  },
                ],
                schema: [
                  {
                    type: FormUIOptions.Text,
                    name: 'otherIncomeName',
                    label: 'Other Income',
                    small: true,
                  },
                  {
                    type: FormUIOptions.UnitMoney,
                    label: 'Amount',
                    layout: 'default',
                    name: 'otherIncomeAmount',
                    unit: '$',
                    small: true,
                  },
                ],
              },
              {
                type: FormUIOptions.Money,
                label: 'Gross Income',
                name: 'monthlyGOI',
                emphasize: true,
                groupStart: true,
                tooltipHeading: 'Gross Income (GOI)',
                description:
                  'Rental income across all units adjusted for vacancy rate',
                unit: '$',
                formula: (monthlyGOI) => ['=', `${monthlyGOI}`],
                total: true,
              },
              {
                type: FormUIOptions.Money,
                label: 'Expenses',
                layout: 'default',
                name: 'totalMonthlyOperatingExpenses',
                groupEnd: true,
                unit: '$',
                formula: (totalMonthlyOperatingExpensesPurchase) => [
                  '-',
                  `${totalMonthlyOperatingExpensesPurchase}`,
                ],
              },
              {
                type: FormUIOptions.Table,
                label: '',
                name: 'operatingExpenses',
                condition: [
                  {
                    field: 'cashPurchase',
                    value: false,
                  },
                  {
                    field: 'enterTotalExpenses',
                    value: [false, undefined, ''],
                  },
                ],
                schema: [
                  {
                    type: FormUIOptions.Text,
                    name: 'expenseName',
                    label: 'Expense',
                    small: true,
                  },
                  {
                    type: FormUIOptions.UnitMoney,
                    derivedFrom: 'rentalIncomePreVacancy',
                    label: 'Amount',
                    layout: 'default',
                    name: 'expenseAmount',
                    unit: '$',
                    small: true,
                  },
                ],
              },
              {
                type: FormUIOptions.Money,
                label: 'Net Income',
                layout: 'default',
                name: 'netMonthlyIncome',
                groupStart: true,
                emphasize: true,
                tooltipHeading: 'Net Income (NOI)',
                description:
                  'Income from all units adjusted for total operating expenses',
                unit: '$',
                formula: (monthlyNOI) => ['=', `${monthlyNOI}`],
                total: true,
              },
              {
                type: FormUIOptions.Money,
                label: 'Loan Payment',
                name: 'newMonthlyPaymentWithRehab',
                unit: '$',
                formula: (monthlyDebtService) => ['-', `${monthlyDebtService}`],
              },
              {
                type: FormUIOptions.Money,
                label: 'Cash Flow',
                layout: 'default',
                name: 'newMonthlyPreTaxCashFlow',
                emphasize: true,
                dynamic: true,
                unit: '$',
                formula: (annualPreTaxCashFlow) => [
                  '=',
                  `${annualPreTaxCashFlow}`,
                ],
              },
            ],
          },
        ],
      },
      {
        sectionType: 'comparison',
        sectionLabel: 'Investment Comparison',
        sectionValue: 'investment-comparison',
        //for now u gotta put three schemas
        schemas: [
          {
            sectionLabel: 'On Purchase',
            annualValue: true,
            afterValues: false,
            schema: [
              {
                type: FormUIOptions.Money,
                label: 'Down Payment',
                name: 'downPaymentAmount',
              },
              {
                type: FormUIOptions.Money,
                label: 'Closing Costs',
                groupEnd: true,
                name: 'totalCashRequiredToClose',

                formula: (totalCashRequiredToClose) => [
                  '+',
                  totalCashRequiredToClose,
                ],
              },
              {
                type: FormUIOptions.Table,
                label: '',
                name: 'closingCosts',
                condition: [
                  {
                    field: 'cashPurchase',
                    value: false,
                  },
                  {
                    field: 'enterTotalClosingCosts',
                    value: [false, undefined, ''],
                  },
                ],
                schema: [
                  {
                    type: FormUIOptions.Text,
                    name: 'closingCostName',
                    label: 'Closing Cost',
                    small: true,
                  },
                  {
                    type: FormUIOptions.Money,
                    label: 'Amount',
                    layout: 'default',
                    name: 'closingCostAmount',
                    unit: '$',
                    small: true,
                  },
                ],
              },
              {
                type: FormUIOptions.Money,
                label: 'Cash Required for Purchase',
                total: true,
                groupStart: true,
                emphasize: true,
                name: 'totalCashInvestedOnPurchase',
                formula: (totalCashInvestedOnPurchase) => [
                  '=',
                  totalCashInvestedOnPurchase,
                ],
              },
            ],
          },
          {
            sectionLabel: 'Buy & Hold (After Rehab)',
            annualValue: true,
            afterValues: true,
            schema: [
              {
                type: FormUIOptions.Money,
                label: 'Down Payment',
                name: 'downPaymentAmount',
              },
              {
                type: FormUIOptions.Money,
                label: 'Closing Costs',
                groupEnd: true,
                name: 'totalCashRequiredToClose',

                formula: (totalCashRequiredToClose) => [
                  '+',
                  totalCashRequiredToClose,
                ],
              },
              {
                type: FormUIOptions.Table,
                label: '',
                name: 'closingCosts',
                condition: [
                  {
                    field: 'cashPurchase',
                    value: false,
                  },
                  {
                    field: 'enterTotalClosingCosts',
                    value: [false, undefined, ''],
                  },
                ],
                schema: [
                  {
                    type: FormUIOptions.Text,
                    name: 'closingCostName',
                    label: 'Closing Cost',
                    small: true,
                  },
                  {
                    type: FormUIOptions.Money,
                    label: 'Amount',
                    layout: 'default',
                    name: 'closingCostAmount',
                    unit: '$',
                    small: true,
                  },
                ],
              },
              {
                type: FormUIOptions.Money,
                label: 'Cash Required for Purchase',
                total: true,
                groupStart: true,
                emphasize: true,
                name: 'totalCashInvestedOnPurchase',
                formula: (totalCashInvestedOnPurchase) => [
                  '=',
                  totalCashInvestedOnPurchase,
                ],
              },
              {
                type: FormUIOptions.Money,
                label: 'Rehab Costs',
                name: 'totalRehabCosts',
                formula: (totalRehabCosts) => ['+', totalRehabCosts],
              },
              {
                type: FormUIOptions.Money,
                label: 'Carrying Costs',
                name: 'totalCarryingCosts',
                formula: (totalCarryingCosts) => ['+', totalCarryingCosts],
              },
              {
                type: FormUIOptions.Money,
                label: 'Rehab Financing',
                condition: {
                  field: 'isRehabFinanced',
                  value: true,
                },
                name: 'totalRehabFinancingAmount',
                formula: (totalCarryingCosts) => ['-', totalCarryingCosts],
              },
              {
                type: FormUIOptions.Money,
                label: 'Cash Required for Rehab',
                total: true,
                groupStart: true,
                emphasize: true,
                name: 'totalCashInvestedBuyAndHold',
              },
            ],
          },
          {
            sectionLabel: 'BRRRR Scenario',
            annualValue: true,
            afterValues: true,
            schema: [
              {
                type: FormUIOptions.Money,
                label: 'Down Payment',
                name: 'downPaymentAmount',
              },
              {
                type: FormUIOptions.Money,
                label: 'Closing Costs',
                groupEnd: true,
                name: 'totalCashRequiredToClose',

                formula: (totalCashRequiredToClose) => [
                  '+',
                  totalCashRequiredToClose,
                ],
              },
              {
                type: FormUIOptions.Table,
                label: '',
                name: 'closingCosts',
                condition: [
                  {
                    field: 'cashPurchase',
                    value: false,
                  },
                  {
                    field: 'enterTotalClosingCosts',
                    value: [false, undefined, ''],
                  },
                ],
                schema: [
                  {
                    type: FormUIOptions.Text,
                    name: 'closingCostName',
                    label: 'Closing Cost',
                    small: true,
                  },
                  {
                    type: FormUIOptions.Money,
                    label: 'Amount',
                    layout: 'default',
                    name: 'closingCostAmount',
                    unit: '$',
                    small: true,
                  },
                ],
              },
              {
                type: FormUIOptions.Money,
                label: 'Cash Required for Purchase',
                total: true,
                groupStart: true,
                emphasize: true,
                name: 'totalCashInvestedOnPurchase',
                formula: (totalCashInvestedOnPurchase) => [
                  '=',
                  totalCashInvestedOnPurchase,
                ],
              },
              {
                type: FormUIOptions.Money,
                label: 'Rehab Costs',
                name: 'totalRehabCosts',
                formula: (totalRehabCosts) => ['+', totalRehabCosts],
              },
              {
                type: FormUIOptions.Money,
                label: 'Carrying Costs',
                name: 'totalCarryingCosts',
                formula: (totalCarryingCosts) => ['+', totalCarryingCosts],
              },
              {
                type: FormUIOptions.Money,
                label: 'Rehab Financing',
                name: 'totalRehabFinancingAmount',
                condition: {
                  field: 'isRehabFinanced',
                  value: true,
                },
                formula: (totalCarryingCosts) => ['-', totalCarryingCosts],
              },
              {
                type: FormUIOptions.Money,
                label: 'Cash Required for Rehab',
                total: true,
                groupStart: true,
                emphasize: true,
                name: 'totalCashInvestedBuyAndHold',
              },
              {
                type: FormUIOptions.Money,
                label: 'Refinance Costs',
                groupEnd: true,
                name: 'totalRefinancingCosts',
                formula: (totalRefinanceCosts) => ['+', totalRefinanceCosts],
              },
              {
                type: FormUIOptions.Table,
                label: '',
                name: 'refinancingCosts',
                schema: [
                  {
                    type: FormUIOptions.Text,
                    name: 'costName',
                    label: 'Closing Cost',
                    small: true,
                  },
                  {
                    type: FormUIOptions.Money,
                    label: 'Amount',
                    layout: 'default',
                    name: 'costAmount',
                    unit: '$',
                    small: true,
                  },
                ],
              },
              {
                type: FormUIOptions.Money,
                label: 'Original Mortgage',
                name: 'totalMortgageAmountNoCMHC',
                groupStart: true,
                tooltipHeading: 'Original Loan',
                description: 'Mortgage before refinancing',
                unit: '$',
                formula: (purchasePrice) => [`+`, `${purchasePrice}`],
              },
              {
                type: FormUIOptions.Money,
                label: 'Rehab Financing',
                name: 'totalRehabFinancingAmount',
                formula: (totalRehabFinancingAmount) => [
                  '+',
                  totalRehabFinancingAmount,
                ],
                condition: {
                  field: 'isRehabFinanced',
                  value: true,
                },
              },
              {
                type: FormUIOptions.Money,
                label: 'Insurance',
                name: 'cmhcPremiumNew',
                unit: '$',
                formula: (cmhcPremiumNew) => [`+`, `${cmhcPremiumNew}`],
                condition: {
                  field: 'shouldCalculateCMHCNew',
                  value: true,
                },
              },
              {
                type: FormUIOptions.Money,
                label: 'New Loan(s)',
                name: 'newFinancingTotal',
                formula: (newFinancingTotal) => [`-`, `${newFinancingTotal}`],
              },
              {
                type: FormUIOptions.Money,
                label: ['Cash / Profit', 'Cash Left In'],
                name: 'cashOut',
                emphasize: true,
                total: true,
                dynamic: true,
                absoluteValue: true,
                unit: '$',
                formula: (cashOut) => [`=`, `${cashOut}`],
              },
            ],
          },
        ],
      },
    ],
  },
};
